import { Fragment, useState } from 'react';

import { saveSmsRunRating } from '@apps/web/components/phoebe_chat_view/conversation/chat_backend_adapter';
import { MessageRatingButtons } from '@apps/web/components/shared/messages/message_rating_buttons';
import {
    RUN_FEEDBACK_DOWNVOTE_PLACEHOLDER,
    RunFeedbackPrompt,
} from '@apps/web/components/shared/run_feedback/run_feedback_prompt';
import { toast } from '@apps/web/lib/app_notifications';
import { type MessageRatingLabel } from '@libraries/typescript/api_client';

// Coordinator training thumbs for a single SMS engine run, anchored on the
// inbound message that triggered it. Thumbs reuse the shared rating buttons;
// the thumbs-down note uses the same RunFeedbackPrompt (Save + required note)
// as the Teach Phoebe feed, so the down-vote flow is uniform across surfaces.
export function SmsRunRatingControl({
    organizationId,
    runId,
    initialLabel,
    label,
    onSaved,
}: {
    readonly organizationId: string;
    readonly runId: string;
    readonly initialLabel: MessageRatingLabel | null;
    // Shown only when several runs anchor to one message, to tell them apart.
    readonly label?: string;
    // Revalidate the run-ratings cache after a write so navigating away and
    // back reflects the saved rating instead of the stale pre-save label.
    readonly onSaved?: () => void;
}) {
    const [selectedLabel, setSelectedLabel] =
        useState<MessageRatingLabel | null>(initialLabel);
    const [draftComment, setDraftComment] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    // Opened only by a thumbs-down this session — never derived from an
    // already-'bad' rating, so a previously down-voted run doesn't show a
    // permanently-open, un-dismissible empty note box.
    const [isNoteOpen, setIsNoteOpen] = useState(false);

    const persist = async (
        ratingLabel: MessageRatingLabel,
        comment: string,
    ): Promise<boolean> => {
        setIsSaving(true);
        try {
            await saveSmsRunRating({
                organizationId,
                runId,
                label: ratingLabel,
                comment: comment || undefined,
            });
            onSaved?.();
            return true;
        } catch {
            toast.error('Failed to save the review. Please try again.', {
                persistNotification: false,
            });
            return false;
        } finally {
            setIsSaving(false);
        }
    };

    const handleLabelClick = async (
        ratingLabel: MessageRatingLabel,
    ): Promise<void> => {
        // A thumbs-down opens the required-note editor rather than persisting a
        // bare negative. A thumbs-up saves as-is and dismisses any open note.
        if (ratingLabel === 'bad') {
            setDraftComment('');
            setIsNoteOpen(true);
            return;
        }
        setIsNoteOpen(false);
        if (await persist('good', '')) {
            setSelectedLabel('good');
        }
    };

    const handleSaveNote = async (): Promise<void> => {
        const comment = draftComment.trim();
        if (!comment) {
            return;
        }
        if (await persist('bad', comment)) {
            setSelectedLabel('bad');
            setIsNoteOpen(false);
        }
    };

    return (
        <Fragment>
            {label !== undefined && (
                <span className="text-muted-foreground px-1 text-xs font-medium">
                    {label}
                </span>
            )}
            <div className="p-1">
                <MessageRatingButtons
                    selectedLabel={selectedLabel}
                    isSaving={isSaving}
                    isOperationPending={false}
                    onLabelClick={handleLabelClick}
                />
            </div>
            {isNoteOpen && (
                <RunFeedbackPrompt
                    className="mt-1"
                    state="note"
                    comment={draftComment}
                    disabled={isSaving}
                    requireComment={true}
                    autoFocusComment={true}
                    submitOnEnter={true}
                    commentPlaceholder={RUN_FEEDBACK_DOWNVOTE_PLACEHOLDER}
                    onCommentChange={setDraftComment}
                    onSaveComment={() => void handleSaveNote()}
                    onSkipComment={() => {
                        setIsNoteOpen(false);
                        setDraftComment('');
                    }}
                    onShowNote={() => undefined}
                />
            )}
        </Fragment>
    );
}
